<template>
  <div>

    <div class="container-fluid">
      <div class="row mt-4">

        <div class="col-md-7 mt-2">
          <h5><strong>Notas de entrada</strong></h5>
        </div>

        <div class="col-md-5 text-right">
          <span v-if="processing" class="last-update processing">
            <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
            Calculando os totais no banco de dados...
          </span>
          <strong v-else-if="updatedAt" class="last-update">Atualizado {{ updatedAt }}</strong>
          <button @click="find(true)" class="btn btn-primary ml-4"><RefreshCcwIcon/></button>
          <router-link
              tag="button"
              to="/dfes"
              class="btn btn-primary btn-xl ml-3"
          >
            Acessar aplicativo
          </router-link>
        </div>

      </div>
    </div>

    <div class="container-fluid">

      <div class="row">
        <div class="col-md-4">
          <div class="card card-status-top green-indicator">
            <p><strong>AUTORIZADAS</strong></p>
            <h3><strong v-if="processing">&mdash;</strong><strong v-else>{{ values.totalAuthorized }}</strong></h3>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card card-status-top red-indicator">
            <p><strong>CANCELADAS</strong></p>
            <h3><strong v-if="processing">&mdash;</strong><strong v-else>{{ values.totalCanceled }}</strong></h3>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card card-status-top blue-indicator">
            <p><strong>TOTAL DE NOTAS</strong></p>
            <h3><strong v-if="processing">&mdash;</strong><strong v-else>{{ values.total }}</strong></h3>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-3">
          <div class="card card-status-bottom">
            <p><strong>VALOR TOTAL AUTORIZADO</strong></p>
            <h5><strong v-if="processing">&mdash;</strong><strong v-else>{{ values.totalValueAuthorized | currency }}</strong></h5>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card card-status-bottom">
            <p><strong>VALOR TOTAL CANCELADO</strong></p>
            <h5><strong v-if="processing">&mdash;</strong><strong v-else>{{ values.totalValueCanceled | currency }}</strong></h5>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card card-status-bottom">
            <p><strong>NOTAS MANIFESTADAS</strong></p>
            <h5><strong v-if="processing">&mdash;</strong><strong v-else>{{ values.totalWithManifest }}</strong></h5>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card card-status-bottom">
            <p><strong>SEM MANIFESTAÇÃO</strong></p>
            <h5><strong v-if="processing">&mdash;</strong><strong v-else>{{ values.totalWithoutManifest }}</strong></h5>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12 mt-4">
          <h5><strong>Recebidas por estabelecimento</strong></h5>
          <p class="period-note">Últimos {{ periodMonths }} meses</p>
        </div>

        <div v-if="!establishmentsLoaded && !establishmentsProcessing" class="col-md-12 processing-box">
          <button @click="loadEstablishments()" class="btn btn-primary">Carregar dados por estabelecimento</button>
        </div>

        <div v-if="establishmentsProcessing" class="col-md-12 processing-box">
          <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
          Calculando os dados por estabelecimento. Em contas com muitas notas isso pode levar alguns minutos.
        </div>

        <div v-if="establishmentsLoaded && establishments.length === 0" class="col-md-12 processing-box">
          Nenhum estabelecimento com notas recebidas nos últimos {{ periodMonths }} meses.
        </div>

        <table v-if="establishmentsLoaded && establishments.length > 0" class="table table-borderless table-border table-align col-md-12">
          <thead>
            <tr>
              <th>Razão Social</th>
              <th>CNPJ</th>
              <th>Cidade/UF</th>
              <th>Qtd.</th>
              <th>Valor</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="est in paginatedEstablishments" :key="est.cnpj">
              <td><strong>{{ est.name }}</strong></td>
              <td><strong>{{ est.cnpj | cnpj }}</strong></td>
              <td><strong>{{ cityLabel(est) }}</strong></td>
              <td><strong>{{ est.count }}</strong></td>
              <td><strong>{{ est.totalValue | currency }}</strong></td>
            </tr>
          </tbody>
        </table>

      </div>
    </div>

    <div v-if="establishmentsLoaded && establishments.length > 0" class="row">
      <div class="col-md-12">
        <Pagination
          v-model="currentPage"
          :total-count="establishments.length"
          :total-pages="totalPages"
          @pagination="pagination"
        />
      </div>
    </div>

  </div>
</template>

<script>
  import { RefreshCcwIcon } from 'vue-feather-icons'
  import { IBGE } from '@/models/ibge'
  import Pagination from '@/components/shares/specialTag/Pagination'
  import GlobalLoading from '@/mixins/GlobalLoading'

  export default {
    components: { Pagination, RefreshCcwIcon },
    mixins: [GlobalLoading],
    computed: {
      keyUpdatedAt() {
        return 'updatedAtDashboardDfeAt'
      },
      updatedAt() {
        let date = this.updatedAtIso ? new Date(this.updatedAtIso) : null
        if (!date || isNaN(date.getTime()))
          return ''

        let time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }).replace(':', 'h')

        if (date.toDateString() === new Date().toDateString())
          return `às ${time}`

        return `em ${date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} às ${time}`
      },
      paginatedEstablishments() {
        let start = (this.currentPage - 1) * this.perPage
        return this.establishments.slice(start, start + this.perPage)
      },
      totalPages() {
        return Math.ceil(this.establishments.length / this.perPage)
      }
    },
    beforeDestroy() {
      clearTimeout(this.refreshTimer)
      clearTimeout(this.establishmentsTimer)
    },
    created() {
      this.find()
      this.updatedAtIso = this.$ls.get(this.keyUpdatedAt)
    },
    data() {
      return {
        cacheKey: 'dashboardDfeAggregation',
        currentPage: 1,
        establishments: [],
        perPage: 10,
        establishmentsCacheKey: 'dashboardDfeEstablishments',
        establishmentsLoaded: false,
        establishmentsProcessing: false,
        establishmentsTimer: null,
        periodMonths: 12,
        processing: false,
        refreshTimer: null,
        updatedAtIso: null,
        values: {
          total: 0,
          totalAuthorized: 0,
          totalCanceled: 0,
          totalValueAuthorized: 0,
          totalValueCanceled: 0,
          totalWithManifest: 0,
          totalWithoutManifest: 0
        }
      }
    },
    methods: {
      cityLabel(est) {
        let city = IBGE.getByCityCode(est.cityCode)
        if (city)
          return `${city.name}/${city.initials}`
        return est.uf || '-'
      },
      find(force, silent) {
        this.currentPage = 1
        let cached = this.$ls.get(this.cacheKey)

        if (!force && cached) {
          this.values = cached.values
          return
        }

        if (!silent)
          this.openGlobalLoad()

        this.axios.get('dfe/fiscal_documents/aggregation')
          .then(r => this.$parse.toCamelcase(r.data))
          .then(data => {
            this.processing = !!data.processing

            if (this.processing) {
              this.scheduleRetry()
              return
            }

            this.values = {
              total: data.total || 0,
              totalAuthorized: data.totalAuthorized || 0,
              totalCanceled: data.totalCanceled || 0,
              totalValueAuthorized: data.totalValueAuthorized || 0,
              totalValueCanceled: data.totalValueCanceled || 0,
              totalWithManifest: data.totalWithManifest || 0,
              totalWithoutManifest: data.totalWithoutManifest || 0
            }
            this.setUpdatedAt(data.generatedAt)
            this.$ls.set(this.cacheKey, { values: this.values }, { minutes: 5 })
          })
          .finally(() => { if (!silent) this.closeGlobalLoad() })
      },
      loadEstablishments(force) {
        let cached = this.$ls.get(this.establishmentsCacheKey)

        if (!force && cached) {
          this.establishments = cached.establishments
          this.periodMonths = cached.periodMonths || this.periodMonths
          this.establishmentsLoaded = true
          return
        }

        this.establishmentsProcessing = true

        this.axios.get('dfe/fiscal_documents/establishments')
          .then(r => this.$parse.toCamelcase(r.data))
          .then(data => {
            this.periodMonths = data.periodMonths || this.periodMonths

            if (data.processing) {
              this.scheduleEstablishmentsRetry()
              return
            }

            this.establishments = data.byEstablishment || []
            this.establishmentsProcessing = false
            this.establishmentsLoaded = true
            this.currentPage = 1
            this.$ls.set(this.establishmentsCacheKey,
                         { establishments: this.establishments, periodMonths: this.periodMonths },
                         { minutes: 5 })
          })
          .catch(() => { this.establishmentsProcessing = false })
      },
      pagination(page) {
        this.currentPage = page
      },
      scheduleEstablishmentsRetry() {
        clearTimeout(this.establishmentsTimer)
        this.establishmentsTimer = setTimeout(() => this.loadEstablishments(true), 20000)
      },
      scheduleRetry() {
        clearTimeout(this.refreshTimer)
        this.refreshTimer = setTimeout(() => this.find(true, true), 20000)
      },
      setUpdatedAt(generatedAt) {
        let date = generatedAt ? new Date(generatedAt) : new Date()
        if (isNaN(date.getTime()))
          date = new Date()

        this.updatedAtIso = date.toISOString()
        this.$ls.set(this.keyUpdatedAt, this.updatedAtIso)
      }
    },
    name: 'DashboardDfe'
  }
</script>

<style lang="scss" scoped>
  @import "src/assets/_main.scss";

  td{
    height: 56px;
  }

   .last-update{
    font-size: 14px;
    color:#051b307a;
  }

  .processing{
    display: inline-flex;
    align-items: center;
    color: #0d6efd;
  }

  .period-note{
    font-size: 13px;
    color:#051b307a;
    margin: 0;
  }

  .processing-box{
    display: flex;
    align-items: center;
    padding: 24px 16px;
    font-size: 14px;
    color:#051b307a;
  }
  .card{
    font-size: 12px;
  }

</style>
